import type React from "react";
import type { VisibilityState } from "@tanstack/react-table";

import type { RevenueSource } from "@/lib/api";
import type { OrgReportFullRow, OrgReportRow, OrgReportTotals, ReportFeesMoneyV2 } from "@/lib/orgSessionsReport";
import type { ReportCurrencyTotals, ReportSummaryMember } from "@/lib/orgReportTotals";
import { PaymentCell } from "@/components/PaymentStatus";
import { durationUnknownReason } from "@/lib/sessionDurations";
import { formatNumber, moneyV2Display, type MoneyV2, type PaymentMoneyV2 } from "@voltie/localization-web";
import { localization, type Translate } from "@/app/localization";

/* ------------------------------------------------------------------ */
/*  Formatters                                                          */
/* ------------------------------------------------------------------ */
// The table shows catalog text rendered with the active `t`; the client CSV is a data
// contract shared with the server CSV (session_report.py): its headers, values and
// totals stay the fixed English below in every UI language. Amounts are shown only from
// the row's money contract 2 blocks, each in its own currency in the active locale; the
// client CSV writes no amount and nothing is summed on the client. A footer currency line
// shows the server's totals of one currency from one totals object (orgReportTotals.ts):
// finalized and estimated billing apart, an unknown amount never a zero.

/** Table energy: the shared grouped number (LOC-405). The CSV keeps its bare toFixed(3). */
export function kwh(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return formatNumber(v, 3);
}

// The backend hands sessions over as "YYYY-MM-DD HH:MM:SS" (UTC). The table shows the
// day US-style to match the date fields above it; the clock is left alone, and
// unrecognized shapes are printed verbatim rather than guessed at.
export function reportTimestamp(raw: string | null | undefined): string {
  if (!raw) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](.+?)Z?$/.exec(raw.trim());
  if (!m) return raw;
  return `${m[2]}/${m[3]}/${m[1]} ${m[4]}`;
}

/** Seconds → compact human duration ('2h 5m' / '14m' / '45s'). '—' when unknown so a
 *  missing value never masquerades as '0s'. Mirrors the server's _format_duration. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  let total = Math.round(seconds);
  if (total < 0) total = 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/** CSV duration: the table's text, but blank (not '—') when unknown — the server CSV
 *  convention, so a spreadsheet never reads a placeholder as data. */
export function csvDuration(seconds: number | null | undefined): string {
  return seconds === null || seconds === undefined ? "" : formatDuration(seconds);
}

// A unit id like "0910042001280660123222723534" is far too long for the table column.
// When the unit has a custom name we show that; otherwise the last few digits with a
// leading ellipsis (full id stays on hover / in the CSV).
const UNIT_ID_TAIL = 6;

export function shortUnitId(unitId: string): string {
  if (unitId.length <= UNIT_ID_TAIL) return unitId;
  return `…${unitId.slice(-UNIT_ID_TAIL)}`;
}

/* ------------------------------------------------------------------ */
/*  Column model                                                        */
/* ------------------------------------------------------------------ */

/** Data lookups the cell/CSV renderers need from the host component. */
export interface ReportColumnCtx {
  /** unit_id -> custom display name, or null when the unit has no real name. */
  unitName: (unitId: string) => string | null;
}

/** What a table cell needs: the data lookups plus the active catalog's `t`. */
export interface ReportCellCtx extends ReportColumnCtx {
  t: Translate;
}

interface ReportColumnBase {
  /** Stable id — the localStorage visibility key and the ColumnChooser key. */
  id: string;
  /** Catalog key of the table header and the column chooser label. */
  labelKey: string;
  /** Catalog key of the header's hover text (what the figures mean). */
  headerTitleKey?: string;
  align?: "left" | "right";
  /** Render the body cell in monospace (used for the timestamp columns). */
  mono?: boolean;
  /** Table body cell. */
  cell: (r: OrgReportRow, ctx: ReportCellCtx) => React.ReactNode;
  /** Table totals-row cell (omit for columns with no meaningful total). The row's
   *  label is placed by the totals row, never by a column. */
  total?: (totals: OrgReportTotals, t: Translate) => React.ReactNode;
  /** Cell of one currency's totals line, from that currency's served totals only (omit =
   *  blank: the contract has no per-currency value for this column). */
  currencyTotal?: (line: ReportCurrencyTotals, t: Translate) => React.ReactNode;
}

/** A session column (time, unit, driver, energy): shown to every viewer and written by
 *  the client CSV. */
export interface ReportDataColumn extends ReportColumnBase {
  money?: false;
  /** CSV value(s) for this row — plain strings, escaped by the writer. A column that
   *  exports several cells returns one per entry of `csvLabels`. */
  csv: (r: OrgReportRow, ctx: ReportColumnCtx) => string | string[];
  /** CSV header cells, one per exported cell: fixed English, never translated. */
  csvLabels: string[];
  /** CSV values are numbers this module formatted (kWh): written as-is, never
   *  formula-guarded. */
  numeric?: boolean;
  /** CSV totals-row value(s), one per exported cell from the first (omit = blank). */
  csvTotal?: (t: OrgReportTotals) => string | string[];
}

/** An amount, fee or payment column. It is withheld from an organization manager
 *  (session-only view) — the server also drops the underlying data, so this only scopes
 *  the table and chooser (see visibleReportColumns) — and it is never written by the
 *  client CSV (see csvReportColumns). It can have no total over all rows, which may be
 *  in different currencies, only a currencyTotal per currency line. */
export interface ReportMoneyColumn extends ReportColumnBase {
  money: true;
  total?: never;
}

export type SessionReportColumn = ReportDataColumn | ReportMoneyColumn;

export type BilledUnknownCode = "invoice_missing" | "not_finalized" | "failed" | "not_authorized" | "no_pricing";

/** Why a row has no billed amount; null when it has one. The amount stays unknown —
 *  never a 0 — until a finalized invoice exists. */
export function billedUnknownCode(billed: MoneyV2 | null, billingStatus: string | null | undefined): BilledUnknownCode | null {
  if (billed !== null) return null;
  switch ((billingStatus ?? "").trim()) {
    case "finalized":
      return "invoice_missing";
    case "pending":
    case "finalizing":
      return "not_finalized";
    case "failed":
      return "failed";
    case "not_authorized":
      return "not_authorized";
    default:
      return "no_pricing";
  }
}

/** Hover text of a row's "—" Billed cell, in the active catalog; null when it has an amount. */
export function billedUnknownReason(billed: MoneyV2 | null, billingStatus: string | null | undefined, t: Translate = localization.t): string | null {
  const reason = billedUnknownCode(billed, billingStatus);
  return reason ? t("operator.orgs.report.billed_unknown_reason", { reason }) : null;
}

export type FeeUnknownBasis = "legacy_formula" | "not_charged" | "unrecorded";

/** Why a fee member of a row is unknown: no revenue record (legacy formula), nothing
 *  captured, or a recorded revenue block without that member. */
export function feeUnknownBasis(source: RevenueSource | undefined): FeeUnknownBasis {
  switch (source) {
    case "legacy_fallback":
      return "legacy_formula";
    case "uncollected":
    case "free_charging_schedule":
      return "not_charged";
    default:
      return "unrecorded";
  }
}

type ReadRowMoney = { readonly payment: PaymentMoneyV2; readonly fees: ReportFeesMoneyV2 };

const isFullRow = (r: OrgReportRow): r is OrgReportFullRow => r.money.status !== "none";

export const unavailable = (t: Translate) => <span className="opacity-60">{t("common.state.unavailable")}</span>;

/** An amount in its own currency; Unavailable when Intl cannot show that currency. */
const amountText = (amount: MoneyV2, t: Translate): string => moneyV2Display(amount) ?? t("common.state.unavailable");

/** The unknown-amount dash with its reason on hover. */
const unknownDash = (why: string) => <span title={why} className="cursor-help underline decoration-dotted">—</span>;

/** The visible "+N unknown" after a currency line's total that leaves out N sessions
 *  whose amount is unknown; nothing when there are none. */
function unknownMark(unknownRows: number, t: Translate): React.ReactNode {
  if (unknownRows === 0) return null;
  return (
    <span
      title={t("operator.orgs.report.total_unknown_help", { count: unknownRows })}
      className="ml-1 cursor-help whitespace-nowrap font-normal underline decoration-dotted"
      data-unknown-rows={unknownRows}
    >
      {t("operator.orgs.report.total_unknown_mark", { count: unknownRows })}
    </span>
  );
}

/** A summary member on a currency line: the known sum (a known zero is a zero) or, when no
 *  session's amount is known, the unknown dash; then the unknown mark. */
function memberTotal(m: ReportSummaryMember, t: Translate): React.ReactNode {
  return (
    <span className="whitespace-nowrap">
      {m.amount !== null ? amountText(m.amount, t) : unknownDash(t("operator.orgs.report.total_unknown_help", { count: m.unknown_rows }))}
      {unknownMark(m.unknown_rows, t)}
    </span>
  );
}

/** Billed on a currency line: the finalized invoices of that currency and, below them and
 *  never added to them, the estimate when any of its sessions is billed by estimate. */
function billedCurrencyTotal({ summary, finalized_billed: finalized }: ReportCurrencyTotals, t: Translate): React.ReactNode {
  const estimate = summary.billed_estimate;
  const finalizedHelp = t("operator.orgs.report.total_billed_finalized_help", { count: finalized?.rows ?? 0 });
  return (
    <span className="inline-flex flex-col items-end">
      <span data-billed-basis="finalized" className="whitespace-nowrap">
        {finalized ? <span title={finalizedHelp} className="cursor-help">{amountText(finalized.money, t)}</span> : unknownDash(finalizedHelp)}
      </span>
      {estimate.rows + estimate.unknown_rows > 0 && (
        <span data-billed-basis="estimate" className="whitespace-nowrap">
          <span title={t("operator.orgs.report.total_billed_estimate_help", { count: estimate.rows })} className="cursor-help">
            ≈{estimate.amount !== null ? amountText(estimate.amount, t) : "—"}
            <span className="ml-1 text-[10px] uppercase text-amber-600 dark:text-amber-400">
              {t("operator.orgs.report.estimate_badge")}
            </span>
          </span>
          {unknownMark(estimate.unknown_rows, t)}
        </span>
      )}
    </span>
  );
}

/** A money cell of a row whose money blocks were read; Unavailable when this row's money
 *  could not be read (or, defensively, on a manager row). */
function moneyCell(r: OrgReportRow, t: Translate, render: (row: OrgReportFullRow, money: ReadRowMoney) => React.ReactNode): React.ReactNode {
  if (!isFullRow(r) || r.money.status !== "value") return unavailable(t);
  return render(r, r.money);
}

/** A recorded fee, payout or owes amount; a known zero is a zero, an unknown one the dash. */
function feeColumn(
  id: string,
  labelKey: string,
  member: Exclude<keyof ReportFeesMoneyV2, "organization_net">,
  headerTitleKey?: string,
): ReportMoneyColumn {
  return {
    id,
    money: true,
    labelKey,
    headerTitleKey,
    align: "right",
    cell: (r, ctx) => moneyCell(r, ctx.t, (row, { fees }) => {
      const amount = fees[member];
      if (amount !== null) return amountText(amount, ctx.t);
      return unknownDash(ctx.t("operator.orgs.report.fee_unknown_reason", { basis: feeUnknownBasis(row.revenue_source) }));
    }),
    currencyTotal: (line, t) => memberTotal(line.summary[member], t),
  };
}

/** A charging / idle / suspended column. The backend sends only exact values; anything
 *  else is null → "—" in the table (hover says why) and a blank CSV cell. Table and
 *  CSV therefore carry the same figures. */
function splitDurationColumn(
  id: string,
  labelKey: string,
  csvLabel: string,
  seconds: (r: OrgReportRow) => number | null | undefined,
): ReportDataColumn {
  return {
    id,
    labelKey,
    csvLabels: [csvLabel],
    align: "right",
    cell: (r, ctx) => {
      const v = seconds(r);
      if (v !== null && v !== undefined) return formatDuration(v);
      const why = durationUnknownReason(r.duration_breakdown, r.duration_issues, ctx.t);
      return why ? <span title={why} className="cursor-help underline decoration-dotted">—</span> : "—";
    },
    csv: (r) => csvDuration(seconds(r)),
  };
}

// The report's columns, in display order. The timing columns (End / Duration /
// Charging / Idle / Suspended) sit right after Start so the "when / how long" story
// reads left-to-right; the financial breakdown follows. Every column but Suspended is
// shown by default — the ColumnChooser lets an operator change that (choices persist
// per browser).
export const SESSION_REPORT_COLUMNS: SessionReportColumn[] = [
  {
    id: "start",
    labelKey: "operator.orgs.report.column_start",
    csvLabels: ["Session Start (UTC)"],
    mono: true,
    cell: (r) => reportTimestamp(r.start_time),
    csv: (r) => r.start_time ?? "",
  },
  {
    id: "end",
    labelKey: "operator.orgs.report.column_end",
    csvLabels: ["Session End (UTC)"],
    mono: true,
    cell: (r) => reportTimestamp(r.end_time),
    csv: (r) => r.end_time ?? "",
  },
  {
    id: "duration",
    labelKey: "operator.orgs.report.column_duration",
    csvLabels: ["Session Duration"],
    align: "right",
    cell: (r) => formatDuration(r.duration_seconds),
    csv: (r) => csvDuration(r.duration_seconds),
  },
  splitDurationColumn("charging", "operator.orgs.report.column_charging", "Charging Time", (r) => r.charging_seconds),
  splitDurationColumn("idle", "operator.orgs.report.column_idle", "Idle Time", (r) => r.idle_seconds),
  splitDurationColumn("suspended", "operator.orgs.report.column_suspended", "Suspended Time", (r) => r.suspended_seconds),
  {
    id: "unit",
    labelKey: "operator.orgs.report.column_unit",
    cell: (r, ctx) => ctx.unitName(r.unit_id) ?? shortUnitId(r.unit_id),
    // The stable id is its own CSV cell (a rename never changes it); the display name
    // sits next to it, blank for an unnamed unit.
    csvLabels: ["Unit ID", "Unit Name"],
    csv: (r, ctx) => [r.unit_id, ctx.unitName(r.unit_id) ?? ""],
    total: (totals, t) => t("operator.orgs.report.total_sessions", { count: totals.total_sessions }),
    currencyTotal: (line, t) => t("operator.orgs.report.total_sessions", { count: line.summary.rows }),
    csvTotal: (t) => `${t.total_sessions} sessions`,
  },
  {
    id: "driver",
    labelKey: "operator.orgs.report.column_driver",
    csvLabels: ["Driver"],
    cell: (r) => r.customer_name || "—",
    csv: (r) => r.customer_name ?? "",
  },
  {
    id: "energy",
    numeric: true,
    labelKey: "operator.orgs.report.column_energy",
    csvLabels: ["Energy (kWh)"],
    align: "right",
    cell: (r) => kwh(r.energy_kwh),
    csv: (r) => (r.energy_kwh ?? 0).toFixed(3),
    total: (t) => kwh(t.total_energy_kwh),
    csvTotal: (t) => (t.total_energy_kwh ?? 0).toFixed(3),
  },
  {
    id: "billed",
    money: true,
    labelKey: "billing.amount.billed",
    align: "right",
    headerTitleKey: "operator.orgs.report.column_billed_help",
    cell: (r, ctx) => moneyCell(r, ctx.t, (row, { payment }) => {
      if (payment.billed === null) return unknownDash(billedUnknownReason(null, row.billing_status, ctx.t) ?? "");
      const text = amountText(payment.billed, ctx.t);
      if (row.billed_amount_basis !== "estimate") return text;
      return (
        <span title={ctx.t("operator.orgs.report.billed_estimate_help")} className="cursor-help" data-billed-basis="estimate">
          ≈{text}
          <span className="ml-1 text-[10px] uppercase text-amber-600 dark:text-amber-400">
            {ctx.t("operator.orgs.report.estimate_badge")}
          </span>
        </span>
      );
    }),
    currencyTotal: billedCurrencyTotal,
  },
  {
    id: "captured",
    money: true,
    labelKey: "billing.amount.captured",
    align: "right",
    cell: (r, ctx) => moneyCell(r, ctx.t, (_row, { payment }) => (payment.captured === null ? "—" : amountText(payment.captured, ctx.t))),
    // The report's capture, as in the rows' cells; the shared captured group is another figure.
    currencyTotal: (line, t) => memberTotal(line.summary.revenue_captured, t),
  },
  {
    id: "payment",
    money: true,
    labelKey: "operator.orgs.report.column_payment",
    cell: (r, ctx) =>
      isFullRow(r) ? <PaymentCell r={r} amounts={r.money.status === "value" ? r.money.payment : "unavailable"} /> : unavailable(ctx.t),
  },
  feeColumn("stripe", "operator.orgs.report.column_processing_fee", "processing_fee", "operator.orgs.report.column_processing_fee_recorded_help"),
  feeColumn("voltie", "operator.orgs.report.column_voltie_fee", "voltie_fee"),
  feeColumn("payout", "operator.orgs.report.column_payout", "payout_to_organization"),
  feeColumn("owes", "operator.orgs.report.column_owes", "organization_owes_voltie"),
];

// Columns hidden until an operator turns them on.
const HIDDEN_BY_DEFAULT = new Set(["suspended"]);

// Default visibility. A saved choice keeps winning; these defaults fill in only the
// columns a saved config does not mention (e.g. ones added after it was saved).
export const SESSION_REPORT_DEFAULT_VISIBILITY: VisibilityState = Object.fromEntries(
  SESSION_REPORT_COLUMNS.map((c) => [c.id, !HIDDEN_BY_DEFAULT.has(c.id)]),
);

// localStorage key for this table's saved column choices (distinct from the Devices
// table's key so the two don't collide).
export const SESSION_REPORT_COLUMNS_LS_KEY = "orgSessionsReportColumns_v1";

/** The columns currently shown, in display order; a column the visibility state does
 *  not mention takes its default. `columns` scopes the set a viewer may see at all
 *  (an org manager is passed the money-free subset) — a saved visibility choice can
 *  therefore never surface a column outside that scope. */
export function visibleReportColumns(
  visibility: VisibilityState,
  columns: SessionReportColumn[] = SESSION_REPORT_COLUMNS,
): SessionReportColumn[] {
  return columns.filter(
    (c) => (visibility[c.id] ?? SESSION_REPORT_DEFAULT_VISIBILITY[c.id]) !== false,
  );
}

/* ------------------------------------------------------------------ */
/*  Client-side CSV (exports ONLY the visible columns)                  */
/* ------------------------------------------------------------------ */

/** RFC-4180 quoting: wrap in quotes and double embedded quotes when the value holds a
 *  comma, quote, or newline; otherwise emit it as-is. */
export function csvEscape(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR = /[\x00-\x1f\x7f]/;
// eslint-disable-next-line no-control-regex
const LEADING_BLANK = /^[\s\x00-\x1f\x7f]*/;

/** A free-text CSV cell, neutralised against formula execution (CSV injection) with a
 *  leading apostrophe — the same rule as the server CSV (session_report.csv_text): a
 *  value starting with = + - @, or with a control character, or reaching one of those
 *  triggers after leading whitespace/control characters. Not for numeric columns. */
export function csvText(value: string): string {
  if (!value) return value;
  const rest = value.replace(LEADING_BLANK, "");
  if (CONTROL_CHAR.test(value[0]) || /^[=+\-@]/.test(rest)) return `'${value}`;
  return value;
}

function csvCells(c: ReportDataColumn, r: OrgReportRow, ctx: ReportColumnCtx): string[] {
  const v = c.csv(r, ctx);
  const cells = Array.isArray(v) ? v : [v];
  // A numeric column's FIRST cell is the number; any further cell is text.
  return cells.map((cell, i) => (c.numeric && i === 0 ? cell : csvText(cell)));
}

function csvTotalCells(c: ReportDataColumn, t: OrgReportTotals): string[] {
  const width = csvHeader(c).length;
  const v = c.csvTotal ? c.csvTotal(t) : [];
  const given = Array.isArray(v) ? v : [v];
  return Array.from({ length: width }, (_, i) => given[i] ?? "");
}

function csvHeader(c: ReportDataColumn): string[] {
  return c.csvLabels;
}

/** The columns the client CSV writes: the session columns of `columns`, in their order.
 *  Amount, fee and payment columns are left out for every viewer until the dashboard
 *  downloads the server's money contract 2 CSV instead. */
export function csvReportColumns(columns: readonly SessionReportColumn[]): ReportDataColumn[] {
  return columns.filter((c): c is ReportDataColumn => !c.money);
}

/**
 * Build the report CSV from the already-fetched rows + totals, emitting ONLY the
 * given columns in their display order (the visible session columns, see
 * csvReportColumns). The server's full CSV export (all columns) is a separate path
 * used elsewhere.
 */
export function buildReportCsv(
  rows: readonly OrgReportRow[],
  totals: OrgReportTotals | null | undefined,
  columns: readonly ReportDataColumn[],
  ctx: ReportColumnCtx,
): string {
  const lines: string[] = [];
  lines.push(columns.flatMap(csvHeader).map(csvEscape).join(","));
  for (const r of rows) {
    lines.push(columns.flatMap((c) => csvCells(c, r, ctx)).map(csvEscape).join(","));
  }
  if (totals && rows.length > 0) {
    const cells = columns.flatMap((c) => csvTotalCells(c, totals));
    // "TOTAL" goes in the first cell only when it carries no total: like the table,
    // the label never replaces a sum.
    if (cells.length > 0 && cells[0] === "") cells[0] = "TOTAL";
    lines.push(cells.map(csvEscape).join(","));
  }
  // CRLF line endings — the convention Excel expects for CSV.
  return lines.join("\r\n");
}
